import React, { useState, useRef, useCallback } from 'react';
import DatePickerInput from '../form-elements/DatePickerInput';
import Select from '../form-elements/Select';
import { 
  CITIZENSHIP_OPTIONS, 
  EDUCATION_OPTIONS 
} from '../../constants/formOptions';
import { validateField } from '../../utils/validateField';

const SURVEY_QUESTIONS = [
  {
    title: 'Отношение к детям',
    questions: [
      { key: 'q1_1', text: '1.1. Считаете ли вы, что важно учитывать индивидуальные особенности ребенка в воспитании?' },
      { key: 'q1_2', text: '1.2. Готовы ли вы адаптировать свои методы под характер ребенка?' },
      { key: 'q1_3', text: '1.3. Согласны ли вы, что эмоциональная поддержка важнее строгой дисциплины в критических ситуациях?' },
    ]
  },
  {
    title: 'Реакция на сложные ситуации',
    questions: [
      { key: 'q2_1', text: '2.1. Можете ли вы сохранять спокойствие, если ребенок устраивает истерику?' },
      { key: 'q2_2', text: '2.2. Будете ли вы избегать повышения голоса на ребенка, даже если он непослушен?' },
      { key: 'q2_3', text: '2.3. Считаете ли вы допустимым физическое наказание (например, шлепки) в воспитании?' },
    ]
  },
  {
    title: 'Методы воспитания',
    questions: [
      { key: 'q3_1', text: '3.1. Используете ли вы игровые методы для разрешения конфликтов?' },
      { key: 'q3_2', text: '3.2. Поддерживаете ли вы систему поощрений (например, похвала, наклейки) за хорошее поведение?' },
      { key: 'q3_3', text: '3.3. Считаете ли вы важным объяснять ребенку причины запретов?' },
    ]
  },
  {
    title: 'Коммуникация с родителями',
    questions: [
      { key: 'q4_1', text: '4.1. Готовы ли вы обсуждать с родителями все важные решения, касающиеся ребенка?' },
      { key: 'q4_2', text: '4.2. Согласны ли вы с тем, что родители могут корректировать ваши методы воспитания?' },
      { key: 'q4_3', text: '4.3. Сообщите ли вы родителям, если чувствуете, что не справляетесь с ситуацией?' },
    ]
  },
  {
    title: 'Личные качества',
    questions: [
      { key: 'q5_1', text: '5.1. Считаете ли вы себя терпеливым человеком?' },
      { key: 'q5_2', text: '5.2. Можете ли вы быстро переключаться между задачами в условиях многозадачности?' },
      { key: 'q5_3', text: '5.3. Легко ли вы находите общий язык с замкнутыми или стеснительными детьми?' },
    ]
  },
  {
    title: 'Ценности и подход',
    questions: [
      { key: 'q6_1', text: '6.1. Считаете ли вы, что ребенок должен иметь право на собственное мнение?' },
      { key: 'q6_2', text: '6.2. Поддерживаете ли вы идею, что ошибки — это часть обучения?' },
      { key: 'q6_3', text: '6.3. Важно ли для вас, чтобы ребенок чувствовал себя эмоционально безопасно с вами?' },
    ]
  },
];

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}
function scrollToField(ref) {
  if (ref && ref.current && isIOS()) {
    setTimeout(() => {
      ref.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest'
      });
    }, 200);
  }
}

const NannyApplicationForm = ({
  formData,
  errors,
  isSubmitting,
  handleInputChange: parentHandleInputChange,
  handleSubmit,
  isSubmitted,
  fieldRefs
}) => {
  const [openSection, setOpenSection] = useState(null);
  const [focusedField, setFocusedField] = useState(null);

  // Локальный handleInputChange для сброса ошибок location/startDate сразу после ввода
  const handleInputChange = (e) => {
    parentHandleInputChange(e);
    const { name, value } = e.target;
    if (isSubmitted && (name === 'location' || name === 'startDate')) {
      // validateField импортируется сверху
      const error = validateField(name, value, 'nanny');
      if (errors[name] !== error) {
        errors[name] = error;
      }
    }
  };

  // Обработка ответов на вопросы опросника
  const handleSurveyChange = (questionKey, value) => {
    parentHandleInputChange({
      target: {
        name: 'surveyAnswers',
        value: {
          ...formData.surveyAnswers,
          [questionKey]: value
        }
      }
    });
  };

  // Проверяем, заполнены ли первые 5 обязательных полей
  const isFirstStepComplete =
    formData.name &&
    formData.birthDate &&
    formData.citizenship &&
    formData.education &&
    formData.location;

  return (
    <form onSubmit={handleSubmit} className="form-container">
      <div className="form-header">
        <h2>Анкета для кандидата</h2>
      </div>

      <div className={`form-group${isSubmitted && errors && errors.name ? ' error' : ''}`} style={{ position: 'relative' }} ref={fieldRefs?.name}>
        <label className="required-field">Имя:</label>
        <input
          type="text"
          name="name"
          value={formData.name || ''}
          onChange={handleInputChange}
          placeholder="Ваше имя"
          minLength={2}
          onFocus={() => { setFocusedField('name'); scrollToField(fieldRefs?.name); }}
          onBlur={() => setFocusedField(null)}
        />
        {focusedField === 'name' && formData.name && (
          <button
            type="button"
            onMouseDown={e => e.preventDefault()}
            onClick={() => handleInputChange({ target: { name: 'name', value: '' } })}
            style={{ position: 'absolute', right: 8, top: 38, background: 'none', border: 'none', fontSize: 18, color: '#aaa', cursor: 'pointer', zIndex: 2 }}
            aria-label="Очистить"
          >×</button>
        )}
      </div>

      <div className={`form-group${isSubmitted && errors && errors.birthDate ? ' error' : ''}`} ref={fieldRefs?.birthDate}>
        <label className="required-field">Дата рождения:</label>
        <DatePickerInput
          value={formData.birthDate}
          onChange={(e) => handleInputChange({
            target: { name: 'birthDate', value: e.target.value }
          })}
          placeholder="Выберите дату рождения"
          minDate={new Date(1943, 0, 1)}
          maxDate={new Date(2005, 11, 31)}
        />
      </div>

      <div className={`form-group${isSubmitted && errors && errors.citizenship ? ' error' : ''}`} style={{ position: 'relative' }} ref={fieldRefs?.citizenship}>
        <label className="required-field">Гражданство:</label>
        <input
          type="text"
          name="citizenship"
          value={formData.citizenship || ''}
          onChange={handleInputChange}
          placeholder="Укажите гражданство"
          onFocus={() => { setFocusedField('citizenship'); scrollToField(fieldRefs?.citizenship); }}
          onBlur={() => setFocusedField(null)}
        />
        {focusedField === 'citizenship' && formData.citizenship && (
          <button
            type="button"
            onMouseDown={e => e.preventDefault()}
            onClick={() => handleInputChange({ target: { name: 'citizenship', value: '' } })}
            style={{ position: 'absolute', right: 8, top: 38, background: 'none', border: 'none', fontSize: 18, color: '#aaa', cursor: 'pointer', zIndex: 2 }}
            aria-label="Очистить"
          >×</button>
        )}
      </div>

      <div className={`form-group${isSubmitted && errors && errors.education ? ' error' : ''}`} ref={fieldRefs?.education}>
        <label className="required-field">Образование:</label>
        <div className="textarea-with-clear">
          <textarea
            name="education"
            value={formData.education || ''}
            onChange={handleInputChange}
            placeholder="Укажите уровень образования, учебное заведение и т.д."
            onFocus={() => { setFocusedField('education'); scrollToField(fieldRefs?.education); }}
            onBlur={() => setFocusedField(null)}
          />
          {focusedField === 'education' && formData.education && (
            <button
              type="button"
              className="clear-btn"
              onMouseDown={e => e.preventDefault()}
              onClick={() => handleInputChange({ target: { name: 'education', value: '' } })}
              aria-label="Очистить"
            >×</button>
          )}
        </div>
      </div>

      <div className={`form-group${isSubmitted && errors && errors.location ? ' error' : ''}`} style={{ position: 'relative' }} ref={fieldRefs?.location}>
        <label className="required-field">Район проживания:</label>
        <input
          type="text"
          name="location"
          value={formData.location?.address || ''}
          onChange={e => handleInputChange({ target: { name: 'location', value: { ...formData.location, address: e.target.value } } })}
          placeholder="Укажите район проживания"
          onFocus={() => { setFocusedField('location'); scrollToField(fieldRefs?.location); }}
          onBlur={() => setFocusedField(null)}
        />
        {focusedField === 'location' && formData.location?.address && (
          <button
            type="button"
            onMouseDown={e => e.preventDefault()}
            onClick={() => handleInputChange({ target: { name: 'location', value: { ...formData.location, address: '' } } })}
            style={{ position: 'absolute', right: 8, top: 38, background: 'none', border: 'none', fontSize: 18, color: '#aaa', cursor: 'pointer', zIndex: 2 }}
            aria-label="Очистить"
          >×</button>
        )}
      </div>

      <div className={`form-group${isSubmitted && errors && errors.languages ? ' error' : ''}`} ref={fieldRefs?.languages}>
        <label className="required-field">Языки, уровень:</label>
        <div className="textarea-with-clear">
          <textarea
            name="languages"
            value={formData.languages || ''}
            onChange={handleInputChange}
            placeholder="Например: Русский - родной, Английский - B2"
            onFocus={() => { setFocusedField('languages'); scrollToField(fieldRefs?.languages); }}
            onBlur={() => setFocusedField(null)}
          />
          {focusedField === 'languages' && formData.languages && (
            <button
              type="button"
              className="clear-btn"
              onMouseDown={e => e.preventDefault()}
              onClick={() => handleInputChange({ target: { name: 'languages', value: '' } })}
              aria-label="Очистить"
            >×</button>
          )}
        </div>
      </div>

      <div className={`form-group${isSubmitted && errors && errors.experience ? ' error' : ''}`} ref={fieldRefs?.experience}>
        <label className="required-field">Опыт работы:</label>
        <div className="textarea-with-clear">
          <textarea
            name="experience"
            value={formData.experience || ''}
            onChange={handleInputChange}
            placeholder="Опишите ваш опыт работы"
            onFocus={() => { setFocusedField('experience'); scrollToField(fieldRefs?.experience); }}
            onBlur={() => setFocusedField(null)}
          />
          {focusedField === 'experience' && formData.experience && (
            <button
              type="button"
              className="clear-btn"
              onMouseDown={e => e.preventDefault()}
              onClick={() => handleInputChange({ target: { name: 'experience', value: '' } })}
              aria-label="Очистить"
            >×</button>
          )}
        </div>
      </div>

      <div className={`form-group${isSubmitted && errors && errors.skills ? ' error' : ''}`} ref={fieldRefs?.skills}>
        <label className="required-field">Навыки:</label>
        <div className="textarea-with-clear">
          <textarea
            name="skills"
            value={formData.skills || ''}
            onChange={handleInputChange}
            placeholder="Опишите ваши профессиональные навыки"
            onFocus={() => { setFocusedField('skills'); scrollToField(fieldRefs?.skills); }}
            onBlur={() => setFocusedField(null)}
          />
          {focusedField === 'skills' && formData.skills && (
            <button
              type="button"
              className="clear-btn"
              onMouseDown={e => e.preventDefault()}
              onClick={() => handleInputChange({ target: { name: 'skills', value: '' } })}
              aria-label="Очистить"
            >×</button>
          )}
        </div>
      </div>

      <div className={`form-group${isSubmitted && errors && errors.aboutMe ? ' error' : ''}`} ref={fieldRefs?.aboutMe}>
        <label className="required-field">О себе:</label>
        <div className="textarea-with-clear">
          <textarea
            name="aboutMe"
            value={formData.aboutMe || ''}
            onChange={handleInputChange}
            placeholder="Расскажите немного о себе"
            onFocus={() => { setFocusedField('aboutMe'); scrollToField(fieldRefs?.aboutMe); }}
            onBlur={() => setFocusedField(null)}
          />
          {focusedField === 'aboutMe' && formData.aboutMe && (
            <button
              type="button"
              className="clear-btn"
              onMouseDown={e => e.preventDefault()}
              onClick={() => handleInputChange({ target: { name: 'aboutMe', value: '' } })}
              aria-label="Очистить"
            >×</button>
          )}
        </div>
      </div>

      <div className={`form-group${isSubmitted && errors && errors.desiredSchedule ? ' error' : ''}`} style={{ position: 'relative' }} ref={fieldRefs?.desiredSchedule}>
        <label className="required-field">Желаемый график работы:</label>
        <input
          type="text"
          name="desiredSchedule"
          value={formData.desiredSchedule || ''}
          onChange={handleInputChange}
          placeholder="Например: 5/2, с 9 до 18"
          onFocus={() => { setFocusedField('desiredSchedule'); scrollToField(fieldRefs?.desiredSchedule); }}
          onBlur={() => setFocusedField(null)}
        />
        {focusedField === 'desiredSchedule' && formData.desiredSchedule && (
          <button
            type="button"
            onMouseDown={e => e.preventDefault()}
            onClick={() => handleInputChange({ target: { name: 'desiredSchedule', value: '' } })}
            style={{ position: 'absolute', right: 8, top: 38, background: 'none', border: 'none', fontSize: 18, color: '#aaa', cursor: 'pointer', zIndex: 2 }}
            aria-label="Очистить"
          >×</button>
        )}
      </div>

      <div className={`form-group${isSubmitted && errors && errors.desiredSalary ? ' error' : ''}`} style={{ position: 'relative' }} ref={fieldRefs?.desiredSalary}>
        <label className="required-field">Желаемая оплата:</label>
        <input
          type="text"
          name="desiredSalary"
          value={formData.desiredSalary || ''}
          onChange={handleInputChange}
          placeholder="Укажите желаемую оплату"
          onFocus={() => { setFocusedField('desiredSalary'); scrollToField(fieldRefs?.desiredSalary); }}
          onBlur={() => setFocusedField(null)}
        />
        {focusedField === 'desiredSalary' && formData.desiredSalary && (
          <button
            type="button"
            onMouseDown={e => e.preventDefault()}
            onClick={() => handleInputChange({ target: { name: 'desiredSalary', value: '' } })}
            style={{ position: 'absolute', right: 8, top: 38, background: 'none', border: 'none', fontSize: 18, color: '#aaa', cursor: 'pointer', zIndex: 2 }}
            aria-label="Очистить"
          >×</button>
        )}
      </div>

      <div className={`form-group${isSubmitted && errors && errors.startDate ? ' error' : ''}`} ref={fieldRefs?.startDate}>
        <label className="required-field">Когда готовы выйти на работу:</label>
        <div className="input-with-clear">
          <input
            type="text"
            name="startDate"
            value={formData.startDate || ''}
            onChange={handleInputChange}
            placeholder="Например: Готова приступить немедленно"
            onFocus={() => { setFocusedField('startDate'); scrollToField(fieldRefs?.startDate); }}
            onBlur={() => setFocusedField(null)}
          />
          {focusedField === 'startDate' && formData.startDate && (
            <button
              type="button"
              className="clear-btn-large-input"
              onMouseDown={e => e.preventDefault()}
              onClick={() => handleInputChange({ target: { name: 'startDate', value: '' } })}
              aria-label="Очистить"
            >×</button>
          )}
        </div>
      </div>

      <div className={`form-group${isSubmitted && errors && errors.contacts ? ' error' : ''}`} style={{ position: 'relative' }} ref={fieldRefs?.contacts}>
        <label className="required-field">Контакты:</label>
        <input
          type="text"
          name="contacts"
          value={formData.contacts || ''}
          onChange={handleInputChange}
          placeholder="Телефон или другой способ связи"
          onFocus={() => { setFocusedField('contacts'); scrollToField(fieldRefs?.contacts); }}
          onBlur={() => setFocusedField(null)}
        />
        {focusedField === 'contacts' && formData.contacts && (
          <button
            type="button"
            onMouseDown={e => e.preventDefault()}
            onClick={() => handleInputChange({ target: { name: 'contacts', value: '' } })}
            style={{ position: 'absolute', right: 8, top: 38, background: 'none', border: 'none', fontSize: 18, color: '#aaa', cursor: 'pointer', zIndex: 2 }}
            aria-label="Очистить"
          >×</button>
        )}
      </div>

      {/* Опросник появляется только если заполнены первые 5 полей */}
      {isFirstStepComplete && (
        <div className={`form-group${isSubmitted && errors && errors.surveyAnswers ? ' error' : ''}`} style={{animation: 'fadeIn 0.5s'}}>
          <h3>Опросник: ваши взгляды и подходы</h3>
          <p style={{fontSize: '14px', color: '#666'}}>Пожалуйста, честно ответьте на вопросы — это поможет подобрать для вас подходящую семью.</p>
          {SURVEY_QUESTIONS.map((section, idx) => (
            <div key={section.title} className="survey-section">
              <button
                type="button"
                className="accordion-toggle"
                onClick={() => setOpenSection(openSection === idx ? null : idx)}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: '10px',
                  background: '#f4f4f5',
                  border: 'none',
                  borderRadius: '6px',
                  marginBottom: '6px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                {section.title}
                <span style={{float: 'right'}}>{openSection === idx ? '▲' : '▼'}</span>
              </button>
              {openSection === idx && (
                <div className="survey-questions" style={{padding: '10px 0 10px 10px'}}>
                  {section.questions.map(q => (
                    <div key={q.key} className="survey-question" style={{marginBottom: '10px'}}>
                      <span style={{display: 'block', marginBottom: '4px'}}>{q.text}</span>
                      <label style={{marginRight: '15px'}}>
                        <input
                          type="radio"
                          name={q.key}
                          value="yes"
                          checked={formData.surveyAnswers?.[q.key] === 'yes'}
                          onChange={() => handleSurveyChange(q.key, 'yes')}
                          required
                        /> Да
                      </label>
                      <label>
                        <input
                          type="radio"
                          name={q.key}
                          value="no"
                          checked={formData.surveyAnswers?.[q.key] === 'no'}
                          onChange={() => handleSurveyChange(q.key, 'no')}
                          required
                        /> Нет
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          {isSubmitted && errors && errors.surveyAnswers && (
            <div className="error-message">{errors.surveyAnswers}</div>
          )}
        </div>
      )}

      <button 
        type="submit" 
        className={`submit-button ${isSubmitting ? 'loading' : ''}`}
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Отправка...' : 'Отправить'}
      </button>
    </form>
  );
};

export default NannyApplicationForm; 